import { createFileRoute, Link } from "@tanstack/react-router";
import { Card, CardDescription, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { fetchAllClients } from "@/lib/api";
import { useEffect, useState } from "react";
import type { SerializedBusiness } from "@/types";
import { EntityAvatar } from "@/components/EntityAvatar";
import { EntityListPage } from "@/components/entity-page/EntityListPage";

export const Route = createFileRoute("/dashboard/clients/")({
  component: ClientsIndexPage,
});

function ClientsIndexPage() {
  return <ClientsIndexPageComponent />;
}

// Exported for testing
export function ClientsIndexPageComponent() {
  const [clients, setClients] = useState<SerializedBusiness[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    fetchAllClients()
      .then((response) => {
        if (!active) return;
        setClients(response.clients);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Error fetching clients:", err);
        setError("Failed to load clients.");
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <EntityListPage
      title="Clients"
      description="Manage clients, their users, and agents."
      items={clients}
      loading={loading}
      error={error}
      getItemKey={(client) => client.id}
      gridClassName="flex flex-col gap-6"
      renderItem={(client) => (
        <Link to="/dashboard/clients/$id" params={{ id: client.id }}>
          <Card className="hover:bg-accent transition-colors cursor-pointer">
            <CardHeader>
              <div className="flex items-center gap-3">
                <EntityAvatar type="client" icon={client.icon} name={client.name} />
                <div className="flex-1 min-w-0">
                  <CardTitle>{client.name}</CardTitle>
                  <CardDescription>{client.email}</CardDescription>
                </div>
              </div>
            </CardHeader>
            {client.description && (
              <CardContent>
                <p className="text-sm text-muted-foreground line-clamp-2">{client.description}</p>
              </CardContent>
            )}
          </Card>
        </Link>
      )}
      createHref="/dashboard/clients/new"
      createLabel="Create Client"
      emptyMessage="No clients found. Create your first client to get started."
    />
  );
}
